import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Stethoscope, UserX, Loader2, Check } from "lucide-react";

/**
 * AssignDoctorDialog — a single reusable dialog for setting or changing a
 * patient's Primary Treating Doctor.
 *
 * Handles both the single-patient and bulk-assign flows, and enforces the
 * role rules on the client side too (backend is the source of truth):
 *   • admin / receptionist  → can pick any active doctor OR unassign
 *   • doctor                → can only self-claim (and this dialog is only
 *                             ever opened for them on unassigned patients)
 */
export default function AssignDoctorDialog({
  open, onClose, onSaved,
  // Single-patient mode:
  patient,                  // { id, name, assigned_doctor_id }
  // Bulk mode:
  bulkPatientIds,           // string[] — when provided, dialog switches to bulk
  currentUser,              // { id, name, role }
  // Preloaded team roster to avoid a redundant fetch when opening the dialog
  team,
}) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(patient?.assigned_doctor_id || null);
  const [saving, setSaving] = useState(false);

  const isBulk = Array.isArray(bulkPatientIds) && bulkPatientIds.length > 0;
  const isDoctor = currentUser?.role === "doctor";
  const canUnassign = !isDoctor;  // doctors can never remove or reassign

  useEffect(() => {
    if (open) {
      setQ("");
      setSelected(patient?.assigned_doctor_id || null);
    }
  }, [open, patient]);

  const doctors = useMemo(() => {
    const list = (team || []).filter((t) => t.role === "doctor" || t.role === "admin");
    // Doctors only see themselves as an option
    if (isDoctor) return list.filter((t) => t.id === currentUser?.id);
    const ql = q.trim().toLowerCase();
    return list.filter((t) => !ql || (t.name || "").toLowerCase().includes(ql));
  }, [team, q, isDoctor, currentUser]);

  const withDr = (n) => (/^dr\.?\s+/i.test(n || "") ? n : `Dr. ${n || "Doctor"}`);

  const save = async () => {
    setSaving(true);
    try {
      if (isBulk) {
        const res = await api.post("/patients/bulk-assign-doctor", {
          patient_ids: bulkPatientIds,
          doctor_id: selected,
        });
        toast.success(
          selected
            ? `${res.data.updated} patient${res.data.updated === 1 ? "" : "s"} assigned to ${withDr((team || []).find((t) => t.id === selected)?.name)}`
            : `${res.data.updated} patient${res.data.updated === 1 ? "" : "s"} unassigned`
        );
      } else {
        await api.post(`/patients/${patient.id}/assign-doctor`, {
          doctor_id: selected,
        });
        if (isDoctor) {
          toast.success("This patient is now assigned to you.");
        } else {
          toast.success(
            selected
              ? `${patient.name} → ${withDr((team || []).find((t) => t.id === selected)?.name)}`
              : `${patient.name} unassigned`
          );
        }
      }
      onSaved?.(selected);
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Could not update the treating doctor."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="assign-doctor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-clay" />
            {isDoctor
              ? "Assign this patient to me"
              : isBulk
                ? `Assign primary doctor · ${bulkPatientIds.length} patients`
                : (patient?.assigned_doctor_id ? "Change treating doctor" : "Assign treating doctor")
            }
          </DialogTitle>
        </DialogHeader>

        {!isDoctor && (
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search doctor by name…"
              data-testid="assign-doctor-search"
              className="pl-9"
              autoFocus
            />
          </div>
        )}

        <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-[#E8EAE9]">
          {doctors.length === 0 && (
            <div className="p-4 text-sm text-ink-soft text-center">
              {isDoctor ? "You are the only eligible doctor." : "No doctors match your search."}
            </div>
          )}
          {doctors.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelected(d.id)}
              data-testid={`assign-doctor-option-${d.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#E8EAE9] last:border-b-0 hover:bg-linen/60 flex items-center justify-between ${
                selected === d.id ? "bg-linen/60" : ""
              }`}
            >
              <div>
                <div className="text-sm font-medium text-ink">{withDr(d.name)}</div>
                {d.role === "admin" && (
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">Also admin</div>
                )}
              </div>
              {selected === d.id && <Check className="w-4 h-4 text-pine" />}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          {canUnassign && (patient?.assigned_doctor_id || isBulk) ? (
            <button
              type="button"
              onClick={() => setSelected(null)}
              data-testid="assign-doctor-unassign"
              className={`text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 border transition ${
                selected === null
                  ? "border-[#B94A48] text-[#B94A48] bg-[#FCECEC]"
                  : "border-[#E8EAE9] text-ink-mid hover:bg-linen"
              }`}
            >
              <UserX className="w-3.5 h-3.5" />
              Unassign
            </button>
          ) : <span />}

          <div className="flex gap-2">
            <button
              onClick={onClose}
              data-testid="assign-doctor-cancel"
              className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || (selected === (patient?.assigned_doctor_id || null) && !isBulk)}
              data-testid="assign-doctor-save"
              className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isDoctor ? "Assign to me" : (selected ? "Assign" : "Unassign")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
